import styled from '@emotion/styled'
import React, { FormEvent, useCallback } from 'react'
import { IconCross, IconSearchTN } from '../../../icons'
import {
    getComponentTheme,
    calcRem,
    breakpoint,
    colors,
} from '../../../__styling'
import { DataLayerEventName, SearchEvent } from '@news-mono/web-common'
import { tokens } from '@news-mono/design-tokens'
import { SEARCH_ELECTION_QUERY_PARAM } from '../../../contexts'

type FormProps = {
    onEvent?: (event: SearchEvent) => void
    queryParams: URLSearchParams
    pathname: string
    onSearch: (value: string) => void
}

export const ElectionSearchInput = ({
    onEvent,
    queryParams,
    pathname,
    onSearch,
}: FormProps) => {
    const inputRef = React.useRef<HTMLInputElement>(null)

    const [searchTerm, setSearchTerm] = React.useState('')

    const sanitiseSearchTerm = (value: string) => {
        return value.replace(/[^a-zA-Z0-9\s\-'’]/g, '') // only allow alphanumeric, spaces, hyphens, and apostrophes
    }

    React.useEffect(() => {
        const searchParams = queryParams.get(SEARCH_ELECTION_QUERY_PARAM)
        if (searchParams) {
            setSearchTerm(sanitiseSearchTerm(searchParams))
        }
    }, [queryParams])

    const handleSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault()

            const formData = new FormData(e.target as HTMLFormElement)
            let inputValue =
                formData.get('search')?.toString().toLowerCase().trim() || ''

            inputValue = sanitiseSearchTerm(inputValue)

            setSearchTerm(inputValue)
            onSearch(inputValue)

            if (!inputValue) {
                queryParams.delete(SEARCH_ELECTION_QUERY_PARAM)
                window.history.pushState({ path: pathname }, '', pathname)
                return
            }

            onEvent?.({
                type: DataLayerEventName.search,
                originator: 'ElectionSearch',
                payload: {
                    searchTerm: inputValue,
                },
            })

            queryParams.set(SEARCH_ELECTION_QUERY_PARAM, inputValue)

            const url = pathname + '?' + queryParams.toString()
            window.history.pushState({ path: url }, '', url)
        },
        [onSearch, onEvent, queryParams, pathname],
    )

    React.useEffect(() => {
        const searchParams = queryParams.get(SEARCH_ELECTION_QUERY_PARAM)
        if (searchParams && inputRef.current) {
            inputRef.current.value = searchParams
        }
    }, [queryParams])

    const handleClear = () => {
        setSearchTerm('')
        onSearch('')
        queryParams.delete(SEARCH_ELECTION_QUERY_PARAM)
        window.history.pushState({ path: pathname }, '', pathname)
        onSearch('')
        inputRef.current?.focus()
    }

    const handleInputChange = (i: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = i.target.value

        const sanitisedValue = sanitiseSearchTerm(rawValue)

        if (sanitisedValue !== rawValue) {
            i.preventDefault()
        }

        setSearchTerm(sanitisedValue)

        if (sanitisedValue.trim() === '') {
            handleClear()
        }
    }

    return (
        <StyledElectionInput onSubmit={handleSubmit}>
            <StyledSearchButton type="submit">
                <IconSearchTN
                    fillColour={tokens.thenightly.colors.palette.neutral[70]}
                />
            </StyledSearchButton>
            <input
                placeholder="Search postcode, electorate or candidate"
                type="text"
                name="search"
                minLength={1}
                maxLength={100}
                ref={inputRef}
                value={searchTerm}
                onChange={handleInputChange}
            />
            <ElectionSearchClearButton
                type="reset"
                hasSearchValue={searchTerm.trim() !== ''}
                onClick={handleClear}
            >
                <ElectionSearchCrossIcon label="Clear Search" />
            </ElectionSearchClearButton>
        </StyledElectionInput>
    )
}

const StyledElectionInput = styled.form(({ theme }) => {
    const electionTheme = getComponentTheme(theme, 'electionTheme')

    return {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: calcRem(16),
        background: electionTheme.input.backgroundColor,
        color: electionTheme.input.textColor,
        borderRadius: calcRem(8),
        padding: calcRem(8, 16),
        outline: `1px solid ${electionTheme.input.outlineColor}`,

        input: {
            background: 'inherit',
            border: 'none',
            outline: 'none',
            flexGrow: 1,
            fontFamily: electionTheme.input.fontFamily,
            fontSize: electionTheme.input.fontSize,
            lineHeight: electionTheme.input.lineHeight,
            letterSpacing: electionTheme.input.letterSpacing,
            height: calcRem(40),
        },

        ['&:is(:hover, :focus-within)']: {
            outline: `1px solid ${electionTheme.input.outlineHoverColor}`,
        },
        transition: 'all 0.2s',
    }
})

const StyledSearchButton = styled.button(({ theme }) => {
    return {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        display: 'grid',
        placeItems: 'center',
    }
})

interface ElectionClearButtonProps {
    hasSearchValue: boolean
}

const ElectionSearchClearButton = styled('button')<ElectionClearButtonProps>(
    ({ hasSearchValue, theme }) => [
        {
            padding: calcRem(2, 6),
            background: 'transparent',
            cursor: 'pointer',
            border: 'none',
            lineHeight: 0,
            marginRight: calcRem(5),

            '&:hover path, &:focus path': {
                fill: theme.sectionValues.primaryColor,
            },

            [breakpoint('md')]: {
                marginRight: 0,
            },
        },
        !hasSearchValue && {
            display: 'none',
        },
    ],
)

const ElectionSearchCrossIcon = styled(IconCross)({
    height: calcRem(10),
    width: calcRem(10),
    fill: colors.thewest.greyGorilla,
})
